/** Email/password auth via amazon-cognito-identity-js (SRP — passwords never leave the browser). */
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

import { buildSession, env, save } from "./session";

const userPool = new CognitoUserPool({
  UserPoolId: env.userPoolId,
  ClientId: env.clientId,
});

function _user(email: string) {
  return new CognitoUser({ Username: email, Pool: userPool });
}

function _persist(s: CognitoUserSession) {
  const session = buildSession(
    s.getIdToken().getJwtToken(),
    s.getAccessToken().getJwtToken(),
    s.getRefreshToken().getToken(),
  );
  save(session);
  return session;
}

export function signIn(email: string, password: string): Promise<void> {
  const user = _user(email);
  const auth = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(auth, {
      onSuccess: (s) => { _persist(s); resolve(); },
      onFailure: (err) => reject(err),
      newPasswordRequired: () => reject(new Error("password change required — contact support")),
    });
  });
}

export function signUp(email: string, password: string): Promise<void> {
  return new Promise((resolve, reject) => {
    userPool.signUp(
      email, password,
      [new CognitoUserAttribute({ Name: "email", Value: email })],
      [],
      (err) => (err ? reject(err) : resolve()),
    );
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    _user(email).confirmRegistration(code, true, (err) => (err ? reject(err) : resolve()));
  });
}

export function resendConfirmation(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    _user(email).resendConfirmationCode((err) => (err ? reject(err) : resolve()));
  });
}

export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    _user(email).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    _user(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}
